import React, { useEffect, useState, useRef } from 'react';
import { PriceDisplay } from './PriceDisplay';
import { XIcon } from 'lucide-react';
import type { Subscription } from '../types';
const UpgradeModal = ({
  onClose,
  onConfirm
}) => {
  const modalRef = useRef(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handleConfirm = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://n8n.blend-ai.com/webhook/a73792c5-62e4-472e-b9a7-fa19d16ac23e', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: 838
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get upgrade URL');
      }
      const data = await response.json();
      setUrl(data.url);
    } catch (error) {
      console.error('Error during upgrade:', error);
      setError('Failed to get upgrade URL. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-142">
      <div ref={modalRef} className="bg-white rounded-lg shadow-lg w-full max-w-md p-6" data-id="element-143">
        <div className="flex justify-between items-center mb-4" data-id="element-144">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-145">
            Upgrade Subscription
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-146">
            <XIcon size={20} data-id="element-147" />
          </button>
        </div>
        {loading ? <p className="text-gray-600 mb-4" data-id="element-148">Loading URL...</p> : error ? <p className="text-red-600 mb-4" data-id="element-149">{error}</p> : url ? <div className="mb-4" data-id="element-150">
            <p className="text-gray-600 mb-2" data-id="element-151">
              Upgrade URL (temporary display):
            </p>
            <div className="bg-gray-50 p-3 rounded-md break-all" data-id="element-152">
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800" data-id="element-153">
                {url}
              </a>
            </div>
          </div> : <p className="text-gray-600 mb-4" data-id="element-154">
            Are you sure you want to upgrade your subscription?
          </p>}
        <div className="flex justify-end space-x-3" data-id="element-155">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-156">
            Close
          </button>
          {!url && !loading && <button onClick={handleConfirm} className="px-4 py-2 bg-[#566FFF] text-white rounded-md hover:bg-[#4A5FE6]" data-id="element-157">
              Get Upgrade URL
            </button>}
        </div>
      </div>
    </div>;
};
interface ShopifySubscriptionProps {
  subscription: Subscription;
  onUpgrade: () => void;
}
export const ShopifySubscription = ({
  subscription,
  onUpgrade
}: ShopifySubscriptionProps) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-158">
      <div className={`p-4 sm:p-6 relative ${['inactive', 'not_subscribed', 'incomplete_payment'].includes(subscription.status) ? 'bg-gradient-to-br from-[#EEF0F7] to-[#F5F6FA]' : ''}`} data-id="element-159">
        <div className={`flex flex-col`} data-id="element-160">
          <div className="flex flex-col sm:flex-row justify-between items-start mb-4 sm:mb-6" data-id="element-161">
            <div data-id="element-162">
              <h3 className="text-lg font-medium text-[#222E63]" data-id="element-163">
                Subscription
              </h3>
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-1" data-id="element-164">
                Billed via{' '}
                <a href="https://admin.shopify.com/settings/billing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-800" data-id="element-165">
                  <img src={subscription.billingSource.toLowerCase() === 'stripe' ? 'https://uploadthingy.s3.us-west-1.amazonaws.com/oFWjQnHLsAzupPzuHWxeZz/download.png' : 'https://cdn.shopify.com/shopifycloud/web/assets/v1/favicon-default-6cbad9de243dbae3.ico'} alt={`${subscription.billingSource} logo`} className="w-4 h-4" data-id="element-166" />
                  {subscription.billingSource.charAt(0).toUpperCase() + subscription.billingSource.slice(1)}
                </a>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-167">
            <div data-id="element-168">
              <div className="text-sm text-gray-500 mb-1" data-id="element-169">Billing Period</div>
              <div className="font-medium" data-id="element-170">Monthly</div>
            </div>
            <div data-id="element-171">
              <div className="text-sm text-gray-500 mb-1" data-id="element-172">Amount</div>
              <PriceDisplay amount={subscription.amount} status={subscription.status} data-id="element-173" />
            </div>
          </div>
        </div>
        {['inactive', 'not_subscribed', 'incomplete_payment'].includes(subscription.status) && <>
            <div className="absolute inset-0 bg-white/5 backdrop-blur-[2px]" data-id="element-174" />
            <div className="absolute inset-0 flex items-center justify-center sm:-translate-y-4 z-10" data-id="element-175">
              <button onClick={() => setShowUpgradeModal(true)} className="w-full sm:w-auto mx-4 sm:mx-0 bg-[#566FFF] text-white px-8 py-3 rounded-md text-base font-medium hover:bg-[#4A5FE6] transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-transform" data-id="element-176">
                Upgrade
              </button>
            </div>
          </>}
      </div>
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} onConfirm={() => {}} data-id="element-177" />}
    </div>;
};